"use client"

import { useState } from "react"
import { Star, Loader2, Check } from "lucide-react"

type Props = {
  residencyId: string
  residencyName: string
}

/**
 * No-account review submission form for logged-out visitors. Captures the
 * full review payload + email; backend sends a magic-link verification.
 * On click, the pending row promotes to a real Review with isAnonymous=true.
 *
 * Reviews are the moat. Every barrier to submission costs review depth.
 */
export function AnonymousReviewForm({ residencyId, residencyName }: Props) {
  const [expanded, setExpanded] = useState(false)
  const [email, setEmail] = useState("")
  const [pseudonym, setPseudonym] = useState("")
  const [year, setYear] = useState<string>("")
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [website, setWebsite] = useState("")
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle")
  const [message, setMessage] = useState("")

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (rating < 1) {
      setMessage("Please pick a star rating.")
      setStatus("error")
      return
    }
    if (body.trim().length < 30) {
      setMessage("Review needs to be at least 30 characters — say a little more.")
      setStatus("error")
      return
    }
    setStatus("loading")
    setMessage("")

    try {
      const res = await fetch("/api/reviews/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          residencyId,
          rating,
          title: title.trim(),
          body: body.trim(),
          pseudonym: pseudonym.trim() || undefined,
          year: year ? Number(year) : null,
          website,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        setStatus("done")
        setMessage("Check your inbox — click the verification link and your review goes live.")
      } else {
        setStatus("error")
        setMessage(data.error ?? "Something went wrong. Try again.")
      }
    } catch {
      setStatus("error")
      setMessage("Network error. Try again.")
    }
  }

  if (status === "done") {
    return (
      <section className="mb-8 px-5 py-6 border border-green-200 bg-green-50/50 rounded-2xl">
        <div className="flex items-center gap-2 text-green-700 mb-1.5">
          <Check size={16} />
          <h3 className="text-[15px] font-semibold">Submitted</h3>
        </div>
        <p className="text-[13px] text-[var(--muted-foreground)]">{message}</p>
      </section>
    )
  }

  if (!expanded) {
    return (
      <section className="mb-8 px-5 py-5 sm:px-6 sm:py-6 border border-[var(--border)] bg-white rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-editorial text-xl mb-1 leading-snug">
              Have you been to {residencyName}?
            </h3>
            <p className="text-[13px] text-[var(--muted-foreground)]">
              Drop a review. No account needed — we&apos;ll just confirm your email.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-medium bg-[var(--foreground)] text-white rounded-md hover:opacity-90 transition-opacity"
          >
            <Star size={13} /> Leave a review
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="mb-8 px-5 py-6 sm:px-6 sm:py-7 border border-[var(--border)] bg-white rounded-2xl">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-editorial text-xl mb-0.5 leading-snug">
            Reviewing {residencyName}
          </h3>
          <p className="text-[12px] text-[var(--muted-foreground)]">
            Posted anonymously. We confirm your email to prevent impersonation — your address stays private.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-[12px] text-[var(--muted-foreground)] hover:text-[var(--foreground)] underline-offset-4 hover:underline"
        >
          Cancel
        </button>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {/* honeypot */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="absolute -left-[9999px] h-0 w-0 opacity-0"
          aria-hidden="true"
        />

        {/* Star rating */}
        <div>
          <label className="block text-[13px] font-medium mb-1.5">Overall rating</label>
          <div
            className="flex items-center gap-1"
            onMouseLeave={() => setHoverRating(0)}
          >
            {[1, 2, 3, 4, 5].map((n) => {
              const active = (hoverRating || rating) >= n
              return (
                <button
                  type="button"
                  key={n}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  aria-label={`${n} stars`}
                  className={`p-1.5 rounded transition-colors ${active ? "text-[var(--foreground)]" : "text-[var(--border)] hover:text-[var(--muted-foreground)]"}`}
                >
                  <Star size={22} fill={active ? "currentColor" : "none"} />
                </button>
              )
            })}
            {rating > 0 && (
              <span className="ml-2 text-[12px] text-[var(--muted-foreground)]">{rating} / 5</span>
            )}
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-[13px] font-medium mb-1.5">Headline</label>
          <input
            type="text"
            required
            minLength={3}
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="One line that captures it."
            className="w-full px-3 py-2 text-[14px] border border-[var(--border)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--foreground)]"
          />
        </div>

        {/* Body */}
        <div>
          <label className="block text-[13px] font-medium mb-1.5">Your experience</label>
          <textarea
            required
            minLength={30}
            maxLength={5000}
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What worked, what didn't. Cohort dynamics, facilities, hidden costs, anything the website doesn't tell you. 30 chars minimum."
            className="w-full px-3 py-2 text-[14px] border border-[var(--border)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--foreground)]"
          />
          <p className="mt-1 text-[11px] text-[var(--muted-foreground)]">{body.length} characters</p>
        </div>

        {/* Year + pseudonym + email row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[12px] font-medium mb-1.5">Year you attended</label>
            <input
              type="number"
              min={1990}
              max={new Date().getFullYear()}
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder={String(new Date().getFullYear())}
              className="w-full px-3 py-2 text-[14px] border border-[var(--border)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--foreground)]"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium mb-1.5">
              Pseudonym <span className="text-[var(--muted-foreground)] font-normal">(optional)</span>
            </label>
            <input
              type="text"
              maxLength={60}
              value={pseudonym}
              onChange={(e) => setPseudonym(e.target.value)}
              placeholder="e.g. Painter from Brooklyn"
              className="w-full px-3 py-2 text-[14px] border border-[var(--border)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--foreground)]"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium mb-1.5">Email (private)</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="w-full px-3 py-2 text-[14px] border border-[var(--border)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--foreground)]"
            />
          </div>
        </div>

        {status === "error" && <p className="text-[13px] text-red-600">{message}</p>}

        <div className="flex items-center justify-between pt-2">
          <p className="text-[11px] text-[var(--muted-foreground)] max-w-md">
            We&apos;ll send a confirmation link to verify you. Review goes live as soon as you click it.
          </p>
          <button
            type="submit"
            disabled={status === "loading"}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-medium bg-[var(--foreground)] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {status === "loading" ? <Loader2 size={13} className="animate-spin" /> : <Star size={13} />}
            {status === "loading" ? "Submitting…" : "Submit review"}
          </button>
        </div>
      </form>
    </section>
  )
}
